import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  AlertCircle,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Edit3,
  History,
  Percent,
  RefreshCw,
  Search,
  Ticket,
  Trash2,
  X,
} from "lucide-react";
import {
  BuyAdminCarouselCompetitor,
  BuyAdminCarouselConfigurationInput,
  BuyAdminCarouselListingSnapshot,
  BuyAdminEventMarkup,
  BuyAdminEventMarkupAudit,
  BuyAdminEventListing,
  BuyAdminEventsMarkupSummary,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import {
  formatEventSourceDateMMDD,
  formatEventSourceDateTimeDisplay,
  formatEventSourceTime,
  pickEventDateSource,
} from "../../../utils/eventDateTime";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./EventsMarkup.module.css";

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const LISTINGS_PAGE_SIZE = 10;
const CAROUSEL_COMPETITORS: Array<{ key: BuyAdminCarouselCompetitor; label: string }> = [
  { key: "ticketmaster", label: "Ticketmaster" },
  { key: "seatgeek", label: "SeatGeek" },
  { key: "vivid_seats", label: "Vivid Seats" },
  { key: "stubhub", label: "StubHub" },
  { key: "tickpick", label: "TickPick" },
  { key: "gametime", label: "GameTime" },
];

type CarouselEventConfiguration = {
  listing: BuyAdminCarouselListingSnapshot;
  competitorPrices: Partial<Record<BuyAdminCarouselCompetitor, number>>;
};

type CarouselListingModal = {
  event: BuyAdminEventMarkup;
  listing: BuyAdminCarouselListingSnapshot;
  selectedCompetitors: BuyAdminCarouselCompetitor[];
  priceInputs: Partial<Record<BuyAdminCarouselCompetitor, string>>;
};

const emptySummary: BuyAdminEventsMarkupSummary = {
  event_count: 0,
  date_tbd_count: 0,
  average_markup_percentage: 0,
};

const formatPercent = (value: number | null | undefined) => `${Number(value || 0).toFixed(2)}%`;

const formatCurrency = (value: number | string | null | undefined) => {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
};

const normalizeListingId = (listing: BuyAdminEventListing) => {
  const id = listing.listing_id ?? listing.id ?? 0;
  const parsed = Number.parseInt(String(id), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getListingBasePrice = (listing: BuyAdminEventListing) => {
  const basePrice = listing.retail_price ?? listing.price ?? 0;
  const parsed = Number(basePrice);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getListingMarkupPercentage = (listing: BuyAdminEventListing) => {
  const markup = listing.listing_markup_percentage ?? listing.effective_markup_percentage ??
    listing.event_markup_percentage ?? listing.markup_percentage ?? 0;
  const parsed = Number(markup);
  return Number.isFinite(parsed) ? parsed : 0;
};

const applyMarkup = (basePrice: number, markupPercentage: number) =>
  Number(Math.max(0, basePrice * (1 + markupPercentage / 100)).toFixed(2));

const applyPriceAdjustment = (price: number, adjustment: number) =>
  Number(Math.max(0, price + adjustment).toFixed(2));

const formatEventDate = (row: BuyAdminEventMarkup) =>
  formatEventSourceDateMMDD(pickEventDateSource(row));

const formatEventTime = (row: BuyAdminEventMarkup) => {
  const time = formatEventSourceTime(pickEventDateSource(row));
  return time || "TBD";
};

const formatAuditDateTime = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const isUpcomingEvent = (event: BuyAdminEventMarkup) => {
  const value = event.occurs_at_local || event.occurs_at || event.event_date;
  if (!value) return true;
  const startsAt = new Date(value);
  return Number.isNaN(startsAt.getTime()) || startsAt.getTime() >= Date.now();
};

type SearchTerm = { text: string; scope: "any" | "location" };

/**
 * "houston rockets, Houston, TX" -> rockets + houston anywhere, then "houston" and "tx" in the venue/city.
 * Text before the first comma is split into words that may match any field. Each comma-separated
 * part after it is a location phrase that must match the venue or the city/state. Without commas,
 * two-letter words (state codes) are treated as location terms.
 */
const parseSearchTerms = (value: string): SearchTerm[] => {
  const segments = value.toLowerCase().split(",").map((segment) => segment.trim()).filter(Boolean);
  if (segments.length === 0) return [];
  const terms: SearchTerm[] = [];
  const push = (text: string, scope: SearchTerm["scope"]) => {
    if (text && !terms.some((term) => term.text === text && term.scope === scope)) terms.push({ text, scope });
  };
  segments[0].split(/\s+/).filter(Boolean).forEach((word) => push(word, word.length <= 2 ? "location" : "any"));
  segments.slice(1).forEach((segment) => push(segment, "location"));
  return terms;
};

const eventMatchesAllTerms = (event: BuyAdminEventMarkup, terms: SearchTerm[]): boolean => {
  const location = [event.venue_name, event.place].filter(Boolean).join(" | ").toLowerCase();
  const anywhere = [event.event_name, ...(event.performer_names || []), event.external_id, location]
    .filter(Boolean)
    .join(" | ")
    .toLowerCase();
  return terms.every((term) => (term.scope === "location" ? location : anywhere).includes(term.text));
};

/** Upper bound on rows pulled for a combined search; the backend pages beyond this. */
const MULTI_TERM_FETCH_LIMIT = 2000;

const getActor = () => ({
  changedBy: localStorage.getItem("qa-admin-user") || "",
  role: localStorage.getItem("x-hasura-user-role") || "",
});

const wait = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const BuyAdminEventsMarkup = () => {
  const [rows, setRows] = useState<BuyAdminEventMarkup[]>([]);
  const [summary, setSummary] = useState<BuyAdminEventsMarkupSummary>(emptySummary);
  const [landingComparisonRows, setLandingComparisonRows] = useState<BuyAdminEventMarkup[]>([]);
  const [auditRows, setAuditRows] = useState<BuyAdminEventMarkupAudit[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingLandingSelection, setSavingLandingSelection] = useState(false);
  const [bulkUpdatingMarkup, setBulkUpdatingMarkup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [modalSuccessMessage, setModalSuccessMessage] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [jumpPage, setJumpPage] = useState("1");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchVersion, setSearchVersion] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<BuyAdminEventMarkup | null>(null);
  const [editEvents, setEditEvents] = useState<BuyAdminEventMarkup[]>([]);
  const [selectedEventsById, setSelectedEventsById] = useState<Record<number, BuyAdminEventMarkup>>({});
  const [markupInput, setMarkupInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null);
  const [eventListingsById, setEventListingsById] = useState<Record<number, BuyAdminEventListing[]>>({});
  const [eventListingsTotalById, setEventListingsTotalById] = useState<Record<number, number>>({});
  const [listingsLoadingById, setListingsLoadingById] = useState<Record<number, boolean>>({});
  const [listingsErrorById, setListingsErrorById] = useState<Record<number, string>>({});
  const [listingMarkupInputs, setListingMarkupInputs] = useState<Record<string, string>>({});
  const [savingListingByKey, setSavingListingByKey] = useState<Record<string, boolean>>({});
  const [listingSearchByEventId, setListingSearchByEventId] = useState<Record<number, { section: string; row: string }>>({});
  const [listingPageByEventId, setListingPageByEventId] = useState<Record<number, number>>({});
  const [carouselConfigurationByEventId, setCarouselConfigurationByEventId] = useState<Record<number, CarouselEventConfiguration>>({});
  const [carouselListingModal, setCarouselListingModal] = useState<CarouselListingModal | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const offset = page * pageSize;
  const showInitialLoading = loading && !hasLoadedOnce;
  const paginationLabel = showInitialLoading
    ? `Page ${page + 1} of …`
    : `Page ${page + 1} of ${totalPages.toLocaleString()}`;

  const selectedEvents = useMemo(
    () => Object.values(selectedEventsById),
    [selectedEventsById],
  );
  const selectedEventIds = useMemo(() => new Set(selectedEvents.map((event) => event.event_id)), [selectedEvents]);
  const savedLandingEventIds = useMemo(
    () => new Set(landingComparisonRows.map((event) => event.event_id)),
    [landingComparisonRows],
  );
  const configuredCarouselEventIds = useMemo(
    () => new Set(Object.keys(carouselConfigurationByEventId).map(Number)),
    [carouselConfigurationByEventId],
  );
  const selectedPageRowCount = rows.filter((row) => selectedEventIds.has(row.event_id)).length;

  const allPageRowsSelected = rows.length > 0 && rows.every((row) => selectedEventIds.has(row.event_id));
  const somePageRowsSelected = selectedPageRowCount > 0 && !allPageRowsSelected;

  const selectedEventTitle = useMemo(() => {
    if (editEvents.length > 1) return `${editEvents.length} Events Selected`;
    return selectedEvent?.event_name || "Event Markup";
  }, [editEvents.length, selectedEvent]);

  /**
   * The backend matches the whole search string against one field at a time, so a combined query
   * such as "houston rockets, Houston, TX" finds nothing there. Here we fetch the backend's matches
   * for the most specific single term (one request, longest term first), keep the events where every
   * term matches something (name, performers, venue, city/state or external id) and page locally.
   * Requests run one at a time: parallel searches exhaust the API's database pool.
   */
  const loadCombinedSearch = async (terms: SearchTerm[]) => {
    const dateFilters = { from: fromDate || undefined, to: toDate || undefined };
    // longest first; two-letter tokens (state codes) last since they match the most rows
    // query the backend with the most specific free term first; location terms last (they match the most rows)
    const ordered = [...terms]
      .sort((a, b) => (a.scope === "location" ? 1 : 0) - (b.scope === "location" ? 1 : 0) || b.text.length - a.text.length)
      .map((term) => term.text);
    let fallback: { data: BuyAdminEventMarkup[]; total: number; summary?: BuyAdminEventsMarkupSummary } | null = null;
    let lastError: unknown = null;

    for (const term of ordered) {
      try {
        const response = await buyAdminApi.getEventsMarkup({ limit: MULTI_TERM_FETCH_LIMIT, offset: 0, search: term, ...dateFilters });
        const total = response.pagination.total;
        if (total === 0) continue;
        if (total <= MULTI_TERM_FETCH_LIMIT) {
          fallback = { data: response.data, total, summary: response.summary };
          break; // complete result set for this term: no need to query the others
        }
        if (!fallback || total < fallback.total) fallback = { data: response.data, total, summary: response.summary };
      } catch (error) {
        lastError = error; // try the next term; the API may time out on very common words
      }
    }

    if (!fallback) {
      if (lastError) throw lastError;
      return { rows: [] as BuyAdminEventMarkup[], total: 0, summary: emptySummary, truncated: false };
    }
    const matched = fallback.data.filter((event) => eventMatchesAllTerms(event, terms));
    return {
      rows: matched.slice(offset, offset + pageSize),
      total: matched.length,
      summary: fallback.summary || emptySummary,
      truncated: fallback.total > MULTI_TERM_FETCH_LIMIT,
    };
  };

  const loadEvents = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const terms = parseSearchTerms(search);
      let data: BuyAdminEventMarkup[];
      let totalCount: number;
      let summaryData: BuyAdminEventsMarkupSummary;

      if (terms.length > 1) {
        const combined = await loadCombinedSearch(terms);
        data = combined.rows;
        totalCount = combined.total;
        summaryData = combined.summary;
        if (combined.truncated) {
          setErrorMessage(`Showing matches from the first ${MULTI_TERM_FETCH_LIMIT.toLocaleString()} results. Add a more specific term to narrow the search.`);
        }
      } else {
        const response = await buyAdminApi.getEventsMarkup({
          limit: pageSize,
          offset,
          search: search.trim() || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
        });
        data = response.data;
        totalCount = response.pagination.total;
        summaryData = response.summary || emptySummary;
      }

      const upcomingRows = data.filter(isUpcomingEvent);
      setRows(upcomingRows);
      setTotal(totalCount);
      setSummary(summaryData);
      setHasLoadedOnce(true);
      setExpandedEventId((prev) => data.some((row) => row.event_id === prev) ? prev : null);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load events.");
      setRows([]);
      setTotal(0);
      setSummary(emptySummary);
      setExpandedEventId(null);
    } finally {
      setLoading(false);
    }
  };

  const loadLandingComparisonSelection = async () => {
    try {
      const response = await buyAdminApi.getLandingComparisonSelection();
      const savedRows = (response.data || []).filter(isUpcomingEvent);
      setLandingComparisonRows(savedRows);
      // The update endpoint replaces the complete carousel. Restore the
      // published rows as selected after a fresh login/remount so adding one
      // draft appends to the saved list instead of overwriting it.
      setSelectedEventsById(Object.fromEntries(
        savedRows.map((row) => [row.event_id, row]),
      ));
      setCarouselConfigurationByEventId(savedRows.reduce<Record<number, CarouselEventConfiguration>>((next, row) => {
        if (row.selected_listing) {
          next[row.event_id] = {
            listing: row.selected_listing,
            competitorPrices: row.competitor_prices || {},
          };
        }
        return next;
      }, {}));
    } catch (_error) {
      setLandingComparisonRows([]);
      setSelectedEventsById({});
      setCarouselConfigurationByEventId({});
    }
  };

  const loadAudit = async (eventId: number) => {
    try {
      const response = await buyAdminApi.getEventMarkupAudit(eventId);
      setAuditRows(response.data || []);
    } catch (_error) {
      setAuditRows([]);
    }
  };

  const getListingKey = (eventId: number, listingId: number) => `${eventId}:${listingId}`;

  const normalizeListingsResponse = (response: any) => {
    const payload = response?.data && typeof response.data === "object" ? response.data : response;
    const event = payload?.event ?? response?.event ?? null;
    const listings = Array.isArray(payload?.listings)
      ? payload.listings
      : Array.isArray(response?.listings)
        ? response.listings
        : [];
    const totalEntries = Number(payload?.total_entries ?? response?.total_entries ?? listings.length);

    return {
      event,
      listings,
      totalEntries: Number.isFinite(totalEntries) ? totalEntries : listings.length,
    };
  };

  const loadEventListings = async (event: BuyAdminEventMarkup, forceReload = false) => {
    const eventId = event.event_id;
    if (!forceReload && eventListingsById[eventId] !== undefined && !listingsErrorById[eventId]) {
      return;
    }

    setListingsLoadingById((prev) => ({ ...prev, [eventId]: true }));
    setListingsErrorById((prev) => ({ ...prev, [eventId]: "" }));

    try {
      const response = await buyAdminApi.getEventListings(eventId);
      const normalized = normalizeListingsResponse(response);
      const { listings, totalEntries } = normalized;
      const eventMarkup = Number(normalized.event?.markup_percentage ?? response?.event?.markup_percentage ?? event.markup_percentage ?? 0);
      const normalizedListings = listings.map((listing: BuyAdminEventListing) => {
        const listingId = normalizeListingId(listing);
        const overridePercentage = listing.listing_markup_percentage == null
          ? null
          : Number(listing.listing_markup_percentage);
        return {
          ...listing,
          id: listingId || listing.id,
          listing_id: listing.listing_id ?? listing.id ?? listingId,
          event_id: listing.event_id ?? eventId,
          listing_markup_percentage:
            overridePercentage !== null && Number.isFinite(overridePercentage) ? overridePercentage : null,
          listing_price_adjustment_amount:
            listing.listing_price_adjustment_amount != null
              ? Number(listing.listing_price_adjustment_amount)
              : Number(listing.price_adjustment_amount ?? 0),
          price_adjustment_amount:
            listing.price_adjustment_amount != null
              ? Number(listing.price_adjustment_amount)
              : 0,
          event_markup_percentage: eventMarkup,
          markup_percentage: eventMarkup,
        };
      });

      setEventListingsById((prev) => ({ ...prev, [eventId]: normalizedListings }));
      setEventListingsTotalById((prev) => ({ ...prev, [eventId]: totalEntries }));
      setListingMarkupInputs((prev) => {
        const next = { ...prev };
        normalizedListings.forEach((listing: BuyAdminEventListing) => {
          const listingId = normalizeListingId(listing);
          if (!listingId) return;
          const key = getListingKey(eventId, listingId);
          if (forceReload || next[key] == null) {
            next[key] = String(getListingMarkupPercentage(listing));
          }
        });
        return next;
      });
    } catch (error: any) {
      setListingsErrorById((prev) => ({
        ...prev,
        [eventId]: error?.message || "Failed to load event listings.",
      }));
      setEventListingsById((prev) => ({ ...prev, [eventId]: [] }));
      setEventListingsTotalById((prev) => ({ ...prev, [eventId]: 0 }));
    } finally {
      setListingsLoadingById((prev) => ({ ...prev, [eventId]: false }));
    }
  };

  const toggleEventListings = async (event: BuyAdminEventMarkup) => {
    if (expandedEventId === event.event_id) {
      setExpandedEventId(null);
      return;
    }

    setExpandedEventId(event.event_id);
    await loadEventListings(event);
  };

  const handleListingMarkupChange = (eventId: number, listingId: number, value: string) => {
    setListingMarkupInputs((prev) => ({
      ...prev,
      [getListingKey(eventId, listingId)]: value,
    }));
  };

  const handleListingSearchChange = (eventId: number, field: "section" | "row", value: string) => {
    setListingSearchByEventId((prev) => ({
      ...prev,
      [eventId]: { ...(prev[eventId] ?? { section: "", row: "" }), [field]: value },
    }));
    setListingPageByEventId((prev) => ({ ...prev, [eventId]: 0 }));
  };

  const setListingPage = (eventId: number, pageIndex: number) => {
    setListingPageByEventId((prev) => ({ ...prev, [eventId]: Math.max(0, pageIndex) }));
  };

  const saveListingMarkup = async (event: BuyAdminEventMarkup, listing: BuyAdminEventListing) => {
    const eventId = event.event_id;
    const listingId = normalizeListingId(listing);
    if (!listingId) return;

    const key = getListingKey(eventId, listingId);
    const inputValue = listingMarkupInputs[key] ?? String(getListingMarkupPercentage(listing));
    const markupPercentage = Number(inputValue);

    if (!Number.isFinite(markupPercentage)) {
      setErrorMessage("Listing markup percentage must be numeric. Use a negative value to reduce the price.");
      return;
    }

    setSavingListingByKey((prev) => ({ ...prev, [key]: true }));
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const actor = getActor();
      await buyAdminApi.updateEventListingMarkup(eventId, listingId, {
        markupPercentage,
        changedBy: actor.changedBy,
        role: actor.role,
      });
      await loadEventListings(event, true);
      setSuccessMessage("Listing markup percentage saved.");
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to update listing markup percentage.");
    } finally {
      setSavingListingByKey((prev) => ({ ...prev, [key]: false }));
    }
  };

  const buildCarouselListingSnapshot = (
    event: BuyAdminEventMarkup,
    listing: BuyAdminEventListing,
  ): BuyAdminCarouselListingSnapshot | null => {
    const listingId = normalizeListingId(listing);
    if (!listingId) return null;
    const basePrice = getListingBasePrice(listing);
    const eventMarkup = Number(
      listing.event_markup_percentage ??
      listing.markup_percentage ??
      event.markup_percentage ??
      0,
    );
    const eventMarkedPrice = applyMarkup(basePrice, eventMarkup);
    const displayPrice = Number(
      listing.display_price ??
      listing.customer_price ??
      applyPriceAdjustment(eventMarkedPrice, Number(listing.price_adjustment_amount ?? 0)),
    );
    return {
      listing_id: listingId,
      section: String(listing.section ?? listing.tevo_section_name ?? "").trim() || null,
      row: String(listing.row ?? "").trim() || null,
      seats: String(listing.seats ?? "").trim() || null,
      quantity: Number(listing.quantity ?? listing.available_quantity ?? 0),
      te_price: Number(basePrice.toFixed(2)),
      quickasyst_price: Number(displayPrice.toFixed(2)),
    };
  };

  const openCarouselListing = (
    event: BuyAdminEventMarkup,
    listing: BuyAdminEventListing,
  ) => {
    const snapshot = buildCarouselListingSnapshot(event, listing);
    if (!snapshot) {
      setErrorMessage("This listing does not have a valid listing ID.");
      return;
    }
    const existing = carouselConfigurationByEventId[event.event_id];
    const competitorPrices = existing?.competitorPrices || {};
    const selectedCompetitors = Object.keys(competitorPrices)
      .filter((key): key is BuyAdminCarouselCompetitor =>
        CAROUSEL_COMPETITORS.some((competitor) => competitor.key === key),
      )
      .slice(0, 3);
    setCarouselListingModal({
      event,
      listing: snapshot,
      selectedCompetitors,
      priceInputs: Object.fromEntries(
        Object.entries(competitorPrices).map(([key, value]) => [key, String(value)]),
      ),
    });
    setErrorMessage("");
  };

  const toggleCarouselCompetitor = (competitor: BuyAdminCarouselCompetitor) => {
    setCarouselListingModal((current) => {
      if (!current) return current;
      const isSelected = current.selectedCompetitors.includes(competitor);
      if (!isSelected && current.selectedCompetitors.length >= 3) return current;
      return {
        ...current,
        selectedCompetitors: isSelected
          ? current.selectedCompetitors.filter((key) => key !== competitor)
          : [...current.selectedCompetitors, competitor],
      };
    });
  };

  const saveCarouselListingDraft = async () => {
    if (!carouselListingModal) return;
    const { event, listing, selectedCompetitors, priceInputs } = carouselListingModal;
    if (selectedCompetitors.length !== 3) {
      setErrorMessage("Select exactly 3 competitors.");
      return;
    }
    const competitorPrices: Partial<Record<BuyAdminCarouselCompetitor, number>> = {};
    for (const competitor of selectedCompetitors) {
      const price = Number(priceInputs[competitor]);
      if (!Number.isFinite(price) || price <= 0) {
        const label = CAROUSEL_COMPETITORS.find((item) => item.key === competitor)?.label || competitor;
        setErrorMessage(`Enter a positive price for ${label}.`);
        return;
      }
      competitorPrices[competitor] = Number(price.toFixed(2));
    }

    const isPublished = landingComparisonRows.some((row) => row.event_id === event.event_id);
    if (!isPublished && !selectedEventIds.has(event.event_id) && selectedEvents.length >= 5) {
      setErrorMessage("You can configure up to 5 carousel events.");
      return;
    }

    const nextConfigurationByEventId = {
      ...carouselConfigurationByEventId,
      [event.event_id]: { listing, competitorPrices },
    };

    if (isPublished) {
      setSavingLandingSelection(true);
      setErrorMessage("");
      try {
        const configurations: BuyAdminCarouselConfigurationInput[] = landingComparisonRows.map((row) => {
          const configuration = nextConfigurationByEventId[row.event_id];
          if (!configuration) throw new Error(`Missing carousel configuration for ${row.event_name}.`);
          return {
            eventId: row.event_id,
            listingId: configuration.listing.listing_id,
            competitorPrices: configuration.competitorPrices,
          };
        });
        const response = await buyAdminApi.updateLandingComparisonSelection(configurations, {
          ...getActor(),
          note: `Updated carousel listing and competitor prices for ${event.event_name}`,
        });
        const savedRows = (response.data || []).filter(isUpcomingEvent);
        setLandingComparisonRows(savedRows);
        setCarouselConfigurationByEventId(savedRows.reduce<Record<number, CarouselEventConfiguration>>((next, row) => {
          if (row.selected_listing) {
            next[row.event_id] = {
              listing: row.selected_listing,
              competitorPrices: row.competitor_prices || {},
            };
          }
          return next;
        }, {}));
        setCarouselListingModal(null);
        showTemporarySuccess(`${event.event_name} was updated on the home screen carousel.`);
      } catch (error: any) {
        setErrorMessage(error?.message || `Failed to update ${event.event_name} on the carousel.`);
      } finally {
        setSavingLandingSelection(false);
      }
      return;
    }

    setCarouselConfigurationByEventId(nextConfigurationByEventId);
    setSelectedEventsById((current) => ({ ...current, [event.event_id]: event }));
    setCarouselListingModal(null);
    setErrorMessage("");
    showTemporarySuccess("Saved to carousel items. Click Save Home Carousel Events to publish.");
  };

  useEffect(() => {
    if (successMessage !== "Listing markup percentage saved.") return;

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage("");
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchVersion]);

  useEffect(() => {
    loadLandingComparisonSelection();
  }, []);

  useEffect(() => {
    setJumpPage(String(page + 1));
  }, [page]);

  const handleSearch = () => {
    setPage(0);
    setSearchVersion((prev) => prev + 1);
  };

  const handleReset = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
    setPage(0);
    setSelectedEventsById({});
    setExpandedEventId(null);
    setEventListingsById({});
    setEventListingsTotalById({});
    setListingsLoadingById({});
    setListingsErrorById({});
    setListingMarkupInputs({});
    setSavingListingByKey({});
    setListingSearchByEventId({});
    setListingPageByEventId({});
    setSearchVersion((prev) => prev + 1);
  };

  const handleJumpToPage = () => {
    const parsed = Number.parseInt(jumpPage, 10);
    if (!Number.isFinite(parsed)) {
      setJumpPage(String(page + 1));
      return;
    }
    const nextPage = Math.min(Math.max(parsed, 1), totalPages) - 1;
    setPage(nextPage);
  };

  const togglePageSelection = () => {
    setSelectedEventsById((prev) => {
      if (rows.every((row) => prev[row.event_id])) {
        const next = { ...prev };
        rows.forEach((row) => {
          delete next[row.event_id];
        });
        return next;
      }
      return rows.reduce<Record<number, BuyAdminEventMarkup>>(
        (next, row) => ({ ...next, [row.event_id]: row }),
        { ...prev },
      );
    });
  };

  const toggleRowSelection = (row: BuyAdminEventMarkup) => {
    setSelectedEventsById((prev) => {
      const next = { ...prev };
      if (next[row.event_id]) {
        delete next[row.event_id];
      } else {
        next[row.event_id] = row;
      }
      return next;
    });
  };

  const openEdit = async (event: BuyAdminEventMarkup, targets: BuyAdminEventMarkup[] = [event]) => {
    setSelectedEvent(event);
    setEditEvents(targets);
    setMarkupInput(targets.length === 1 ? String(event.markup_percentage ?? 0) : "");
    setNoteInput("");
    setSuccessMessage("");
    setModalSuccessMessage("");
    setErrorMessage("");
    if (targets.length === 1) {
      await loadAudit(event.event_id);
    } else {
      setAuditRows([]);
    }
  };

  const openSelectedEdit = async () => {
    if (selectedEvents.length === 0) {
      setErrorMessage("Select at least one event to edit.");
      return;
    }
    await openEdit(selectedEvents[0], selectedEvents);
  };

  const closeEdit = () => {
    setSelectedEvent(null);
    setEditEvents([]);
    setAuditRows([]);
    setMarkupInput("");
    setNoteInput("");
    setModalSuccessMessage("");
  };

  const saveMarkup = async () => {
    const targets = editEvents.length > 0 ? editEvents : selectedEvent ? [selectedEvent] : [];
    if (targets.length === 0) return;
    const markup = Number(markupInput);
    if (!Number.isFinite(markup) || markup < 0 || markup > 100) {
      setErrorMessage("Markup percentage must be between 0 and 100.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    setModalSuccessMessage("");
    try {
      const actor = getActor();
      const nextMarkup = Number(markup.toFixed(2));
      for (const event of targets) {
        await buyAdminApi.updateEventMarkup(event.event_id, {
          markupPercentage: nextMarkup,
          changedBy: actor.changedBy,
          role: actor.role,
          note: noteInput.trim(),
        });
        if (eventListingsById[event.event_id] !== undefined) {
          await loadEventListings({ ...event, markup_percentage: nextMarkup }, true);
        }
      }
      const savedMessage = targets.length === 1
        ? "Event markup saved."
        : `Markup saved for ${targets.length} events.`;
      setModalSuccessMessage(savedMessage);
      setSelectedEventsById({});
      await loadEvents();
      await wait(2000);
      closeEdit();
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to update event markup.");
    } finally {
      setSaving(false);
    }
  };

  const showTemporarySuccess = (message: string) => {
    setSuccessMessage(message);
    window.setTimeout(() => {
      setSuccessMessage((current) => current === message ? "" : current);
    }, 3000);
  };
  const [removingCarouselEventId, setRemovingCarouselEventId] = useState<number | null>(null);

  const removeFromCarousel = async (event: BuyAdminEventMarkup) => {
    if (savingLandingSelection || removingCarouselEventId != null) return;
    const isPublished = landingComparisonRows.some((row) => row.event_id === event.event_id);
    if (!isPublished) {
      // only a local draft: drop the configuration, nothing was saved to the carousel yet
      setCarouselConfigurationByEventId((current) => {
        const next = { ...current };
        delete next[event.event_id];
        return next;
      });
      showTemporarySuccess(`Removed ${event.event_name} from the carousel items.`);
      return;
    }

    const confirmation = await Swal.fire({
      title: "Remove carousel event?",
      text: `“${event.event_name}” will be removed from the members save carousel and disappear from the home page immediately.`,
      icon: "warning",
      iconColor: "#f59e0b",
      background: "linear-gradient(135deg, #1b2244 0%, #141a36 100%)",
      color: "#ffffff",
      backdrop: "rgba(0, 0, 0, 0.66)",
      showCancelButton: true,
      confirmButtonText: "Remove event",
      cancelButtonText: "Keep event",
      reverseButtons: true,
      focusCancel: true,
      allowOutsideClick: false,
      customClass: {
        container: "qa-carousel-swal-container",
        popup: "qa-carousel-swal",
      },
    });
    if (!confirmation.isConfirmed) return;

    const remainingRows = landingComparisonRows.filter((row) => row.event_id !== event.event_id);
    const unconfigured = remainingRows.filter((row) => !carouselConfigurationByEventId[row.event_id]);
    if (unconfigured.length > 0) {
      setErrorMessage(
        `Cannot remove yet: ${unconfigured.map((row) => row.event_name).join(", ")} ` +
        "has no listing and competitor prices saved, so the remaining carousel cannot be re-saved. Configure it first.",
      );
      return;
    }

    setRemovingCarouselEventId(event.event_id);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const configurations: BuyAdminCarouselConfigurationInput[] = remainingRows.map((row) => {
        const configuration = carouselConfigurationByEventId[row.event_id];
        return {
          eventId: row.event_id,
          listingId: configuration.listing.listing_id,
          competitorPrices: configuration.competitorPrices,
        };
      });
      const response = await buyAdminApi.updateLandingComparisonSelection(configurations, {
        ...getActor(),
        note: `Removed ${event.event_name} from carousel`,
      });
      const savedRows = response.data || [];
      setLandingComparisonRows(savedRows);
      setCarouselConfigurationByEventId(savedRows.reduce<Record<number, CarouselEventConfiguration>>((next, row) => {
        if (row.selected_listing) {
          next[row.event_id] = {
            listing: row.selected_listing,
            competitorPrices: row.competitor_prices || {},
          };
        }
        return next;
      }, {}));
      setSelectedEventsById((prev) => {
        const next = { ...prev };
        delete next[event.event_id];
        return next;
      });
      showTemporarySuccess(
        savedRows.length
          ? `Removed ${event.event_name} from the carousel. ${savedRows.length} event${savedRows.length === 1 ? "" : "s"} remaining.`
          : `Removed ${event.event_name}. The members save carousel is now empty.`,
      );
    } catch (error: any) {
      setErrorMessage(error?.message || `Failed to remove ${event.event_name} from the carousel.`);
    } finally {
      setRemovingCarouselEventId(null);
    }
  };

  const editCarouselEvent = (event: BuyAdminEventMarkup) => {
    const configuration = carouselConfigurationByEventId[event.event_id];
    if (!configuration) {
      setErrorMessage(`No saved listing configuration was found for ${event.event_name}.`);
      return;
    }
    const listing = configuration.listing;
    openCarouselListing(event, {
      listing_id: listing.listing_id,
      section: listing.section,
      row: listing.row,
      seats: listing.seats,
      quantity: listing.quantity,
      available_quantity: listing.quantity,
      retail_price: listing.te_price,
      display_price: listing.quickasyst_price,
    });
  };

  const saveLandingSelection = async () => {
    if (selectedEvents.length > 5) {
      setErrorMessage("Select up to 5 events for the members save carousel.");
      return;
    }

    setSavingLandingSelection(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const configurations: BuyAdminCarouselConfigurationInput[] = selectedEvents.map((event) => {
        const configuration = carouselConfigurationByEventId[event.event_id];
        if (!configuration) {
          throw new Error(`Choose a listing and 3 competitors for ${event.event_name}.`);
        }
        return {
          eventId: event.event_id,
          listingId: configuration.listing.listing_id,
          competitorPrices: configuration.competitorPrices,
        };
      });
      const actor = getActor();
      const response = await buyAdminApi.updateLandingComparisonSelection(
        configurations,
        actor,
      );
      const savedRows = response.data || [];
      setLandingComparisonRows(savedRows);
      setCarouselConfigurationByEventId(savedRows.reduce<Record<number, CarouselEventConfiguration>>((next, row) => {
        if (row.selected_listing) {
          next[row.event_id] = {
            listing: row.selected_listing,
            competitorPrices: row.competitor_prices || {},
          };
        }
        return next;
      }, {}));
      showTemporarySuccess(
        response.data?.length
          ? `Members save carousel updated with ${response.data.length} events.`
          : "Members save carousel cleared.",
      );
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to save members save carousel events.");
    } finally {
      setSavingLandingSelection(false);
    }
  };

  const setAllUpcomingEventsToFivePercent = async () => {
    const confirmation = await Swal.fire({
      title: "Set all upcoming events to 5%?",
      text: `This will update ${summary.event_count.toLocaleString()} upcoming and date-TBD events. Existing listing-level overrides will remain unchanged.`,
      icon: "question",
      iconColor: "#ffc107",
      background: "linear-gradient(135deg, #1b2244 0%, #141a36 100%)",
      color: "#ffffff",
      backdrop: "rgba(0, 0, 0, 0.66)",
      showCancelButton: true,
      confirmButtonText: "Set all to 5%",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
      allowOutsideClick: false,
      customClass: {
        container: "qa-carousel-swal-container",
        popup: "qa-carousel-swal",
      },
    });
    if (!confirmation.isConfirmed) return;

    setBulkUpdatingMarkup(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const response = await buyAdminApi.bulkUpdateUpcomingEventMarkup({
        markupPercentage: 5,
        ...getActor(),
        note: "Bulk set all upcoming event markup to 5%",
      });
      await loadEvents();
      setSelectedEventsById({});
      showTemporarySuccess(
        `${response.data.updated_count.toLocaleString()} events updated to 5%. ` +
        `${response.data.matched_count.toLocaleString()} upcoming events checked.`,
      );
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to set upcoming events to 5% markup.");
    } finally {
      setBulkUpdatingMarkup(false);
    }
  };

  return (
    <div className={`${styles.page} buy-admin-theme-scope`}>
      <div className={styles.header}>
        <div>
          <div className={styles.titleRow}>
            <CalendarClock size={22} />
            <h1>Events Lookup & Markup</h1>
          </div>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={loadEvents}
            disabled={loading}
            aria-label="Refresh events"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : ""} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.metrics}>
        <div className={`${styles.metric} ${styles.metricEvents}`}>
          <div>
            <span>Upcoming Events</span>
            <strong className={showInitialLoading ? styles.metricLoading : ""}>
              {showInitialLoading ? "—" : summary.event_count.toLocaleString()}
            </strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconEvents}`}><Ticket size={18} /></div>
        </div>
        <div className={`${styles.metric} ${styles.metricMarkup}`}>
          <div>
            <span>Average Markup</span>
            <strong className={showInitialLoading ? styles.metricLoading : ""}>
              {showInitialLoading ? "—" : formatPercent(summary.average_markup_percentage)}
            </strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconMarkup}`}><Percent size={18} /></div>
        </div>
        <div className={`${styles.metric} ${styles.metricTbd}`}>
          <div>
            <span>Date TBD</span>
            <strong className={showInitialLoading ? styles.metricLoading : ""}>
              {showInitialLoading ? "—" : summary.date_tbd_count.toLocaleString()}
            </strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconTbd}`}><CalendarClock size={18} /></div>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.inputGroup}>
          <label>Search</label>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") handleSearch(); }}
            placeholder="event, venue, performer, city, external id — combine terms, e.g. rockets houston tx"
          />
        </div>
        <div className={styles.inputGroup}>
          <label>From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") handleSearch(); }}
          />
        </div>
        <div className={styles.inputGroup}>
          <label>To</label>
          <input
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") handleSearch(); }}
          />
        </div>
        <button className={styles.searchButton} onClick={handleSearch}>
          <Search size={16} />
          Search
        </button>
        <button className={styles.resetButton} onClick={handleReset}>Reset</button>
      </div>

      {errorMessage && <div className={styles.errorBanner}><AlertCircle size={16} />{errorMessage}</div>}
      {successMessage && <div className={styles.successBanner}>{successMessage}</div>}

      <section className={styles.carouselSection} aria-labelledby="home-carousel-heading">
        <div className={styles.carouselSectionHeader}>
          <div>
            <div className={styles.carouselSectionTitleRow}>
              <Ticket size={19} />
              <h2 id="home-carousel-heading">Home Screen Carousel Events</h2>
              <span className={styles.carouselCount}>{landingComparisonRows.length}/5</span>
            </div>
            <p>Published events and their selected listing shown in the app's member savings carousel.</p>
          </div>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={loadLandingComparisonSelection}
            disabled={savingLandingSelection}
            aria-label="Refresh home carousel events"
            title="Refresh home carousel events"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {landingComparisonRows.length === 0 ? (
          <div className={styles.carouselEmptyState}>
            No upcoming events are currently published to the home screen carousel.
          </div>
        ) : (
          <div className={styles.carouselEventGrid}>
            {landingComparisonRows.map((event) => {
              const configuration = carouselConfigurationByEventId[event.event_id];
              const listing = configuration?.listing || event.selected_listing;
              const competitorPrices = configuration?.competitorPrices || event.competitor_prices || {};
              return (
                <article className={styles.carouselEventCard} key={event.event_id}>
                  <div className={styles.carouselEventCardHeader}>
                    <div>
                      <span className={styles.carouselSlot}>Carousel slot {event.slot || "-"}</span>
                      <h3>{event.event_name}</h3>
                      <p>{formatEventDate(event)} at {formatEventTime(event)} · {event.venue_name || event.place || "Venue TBD"}</p>
                    </div>
                    <span className={styles.publishedBadge}>Published</span>
                  </div>

                  {listing && (
                    <div className={styles.carouselListingDetails}>
                      <div><span>Listing ID</span><strong>{listing.listing_id}</strong></div>
                      <div><span>Section / Row</span><strong>{listing.section || "N/A"} / {listing.row || "N/A"}</strong></div>
                      <div><span>Quantity</span><strong>{listing.quantity}</strong></div>
                      <div><span>Member Price</span><strong>{formatCurrency(listing.quickasyst_price)}</strong></div>
                    </div>
                  )}

                  <div className={styles.carouselCompetitors}>
                    {Object.entries(competitorPrices).map(([key, price]) => (
                      <span key={key}>
                        {CAROUSEL_COMPETITORS.find((item) => item.key === key)?.label || key}
                        <strong>{formatCurrency(price)}</strong>
                      </span>
                    ))}
                  </div>

                  <div className={styles.carouselCardActions}>
                    <button type="button" onClick={() => editCarouselEvent(event)} disabled={!listing}>
                      <Edit3 size={15} /> Edit listing & prices
                    </button>
                    <button
                      type="button"
                      className={styles.carouselRemoveButton}
                      onClick={() => void removeFromCarousel(event)}
                      disabled={savingLandingSelection || removingCarouselEventId != null}
                    >
                      <Trash2 size={15} />
                      {removingCarouselEventId === event.event_id ? "Removing..." : "Remove"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div className={styles.tableCard}>
        <div className={styles.selectionBar}>
          <span>
            {selectedEvents.length} selected
            {` • Carousel items configured: ${configuredCarouselEventIds.size}`}
            {` • Carousel saved: ${landingComparisonRows.length}`}
          </span>
          <button
            className={styles.searchButton}
            onClick={openSelectedEdit}
            disabled={selectedEvents.length === 0 || loading || bulkUpdatingMarkup}
            title="Edit markup for all selected events"
          >
            <Edit3 size={16} />
            Edit Markup
          </button>
          <button
            className={styles.bulkMarkupButton}
            onClick={() => void setAllUpcomingEventsToFivePercent()}
            disabled={loading || bulkUpdatingMarkup || summary.event_count === 0}
            title="Set the event-level markup for every upcoming event to 5%"
          >
            <Percent size={16} />
            {bulkUpdatingMarkup ? "Updating All..." : "Set All Upcoming to 5%"}
          </button>
          <button
            className={styles.searchButton}
            onClick={saveLandingSelection}
            disabled={loading || savingLandingSelection || selectedEvents.length > 5}
            title="Save up to 5 selected events to the carousel"
          >
            <Ticket size={16} />
            {savingLandingSelection ? "Saving Carousel..." : "Save Home Carousel Events"}
          </button>
        </div>
        {loading ? (
          <div className={styles.emptyState}>Loading events...</div>
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>No upcoming events found.</div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all events on this page"
                      checked={allPageRowsSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = somePageRowsSelected;
                      }}
                      onChange={togglePageSelection}
                      disabled={loading || rows.length === 0}
                    />
                  </th>
                  <th>Event Name</th>
                  <th>Date (MM/DD/YYYY)</th>
                  <th>Time</th>
                  <th>Place</th>
                  <th>Venue</th>
                  <th>Markup %</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isExpanded = expandedEventId === row.event_id;
                  const listings = eventListingsById[row.event_id] || [];
                  const totalListings = eventListingsTotalById[row.event_id] ?? listings.length;
                  const listingSearch = listingSearchByEventId[row.event_id] ?? { section: "", row: "" };
                  const sectionSearchTerm = listingSearch.section.trim().toLowerCase();
                  const rowSearchTerm = listingSearch.row.trim().toLowerCase();
                  const filteredListings = listings.filter((listing) => {
                    const section = String(listing.section ?? listing.tevo_section_name ?? "").toLowerCase();
                    const rowName = String(listing.row ?? "").toLowerCase();
                    return section.includes(sectionSearchTerm) && rowName.includes(rowSearchTerm);
                  });
                  const listingPageCount = Math.max(1, Math.ceil(filteredListings.length / LISTINGS_PAGE_SIZE));
                  const listingPageIndex = Math.min(listingPageByEventId[row.event_id] ?? 0, listingPageCount - 1);
                  const listingPageStart = listingPageIndex * LISTINGS_PAGE_SIZE;
                  const visibleListings = filteredListings.slice(listingPageStart, listingPageStart + LISTINGS_PAGE_SIZE);
                  const listingPageEnd = Math.min(listingPageStart + visibleListings.length, filteredListings.length);

                  return (
                    <React.Fragment key={row.event_id}>
                      <tr className={isExpanded ? styles.activeRow : undefined}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${row.event_name}`}
                            checked={selectedEventIds.has(row.event_id)}
                            onChange={() => toggleRowSelection(row)}
                          />
                        </td>
                        <td>
                          <strong>{row.event_name}</strong>
                          {savedLandingEventIds.has(row.event_id) && (
                            <div className={styles.subText}>Members save carousel</div>
                          )}
                          {configuredCarouselEventIds.has(row.event_id) && (
                            <div className={styles.carouselConfiguredText}>✓ Listing and 3 competitors configured</div>
                          )}
                          {row.performer_names.length > 0 && (
                            <div className={styles.subText}>{row.performer_names.join(", ")}</div>
                          )}
                        </td>
                        <td>{formatEventDate(row)}</td>
                        <td>{formatEventTime(row)}</td>
                        <td>{row.place}</td>
                        <td>{row.venue_name}</td>
                        <td className={styles.markupCell}>
                          <span className={styles.markupValue}>{formatPercent(row.markup_percentage)}</span>
                        </td>
                        <td>
                          <div className={styles.rowActions}>
                            <button
                              className={styles.iconButton}
                              onClick={() => toggleEventListings(row)}
                              title={isExpanded ? "Hide listings" : "Show listings"}
                              aria-label={isExpanded ? `Hide listings for ${row.event_name}` : `Show listings for ${row.event_name}`}
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            <button
                              className={styles.iconButton}
                              onClick={() => openEdit(
                                row,
                                selectedEventIds.has(row.event_id) && selectedEvents.length > 1 ? selectedEvents : [row],
                              )}
                              title="Edit markup"
                            >
                              <Edit3 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className={styles.expandedRow}>
                          <td colSpan={8}>
                            <div className={styles.expandedContent}>
                              <div className={styles.expandedMeta}>
                                <span>
                                  <span className={styles.expandedMetaLabel}>Event:</span> {row.event_name}
                                </span>
                                <span className={styles.expandedMetaDivider}>|</span>
                                <span>
                                  <span className={styles.expandedMetaLabel}>Event Markup:</span> {formatPercent(row.markup_percentage)}
                                </span>
                                <span className={styles.expandedMetaDivider}>|</span>
                                <span>
                                  <span className={styles.expandedMetaLabel}>Listings:</span> {totalListings.toLocaleString()}
                                </span>
                                <span className={styles.expandedMetaDivider}>|</span>
                                <span>
                                  <span className={styles.expandedMetaLabel}>Scope:</span> event_id + listing_id
                                </span>
                              </div>

                              <div className={styles.expandedHeader}>
                                <div>
                                  <h4>
                                    Event Listings
                                    <span className={styles.seatCountLabel}>({totalListings.toLocaleString()} listings)</span>
                                  </h4>
                                  <p className={styles.expandedHint}>
                                    Custom listing markup percentages only apply to this event and listing ID.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  className={styles.refreshListingsButton}
                                  onClick={() => loadEventListings(row, true)}
                                  disabled={listingsLoadingById[row.event_id]}
                                  aria-label="Refresh listings"
                                  title="Refresh listings"
                                >
                                  <RefreshCw size={14} className={listingsLoadingById[row.event_id] ? styles.spin : ""} />
                                </button>
                              </div>

                              {listingsLoadingById[row.event_id] ? (
                                <div className={styles.listingsEmpty}>Loading listings...</div>
                              ) : listingsErrorById[row.event_id] ? (
                                <div className={styles.errorBanner}>
                                  <AlertCircle size={16} />
                                  {listingsErrorById[row.event_id]}
                                </div>
                              ) : listings.length === 0 ? (
                                <div className={styles.listingsEmpty}>No listings returned for this event.</div>
                              ) : (
                                <>
                                  <div className={styles.listingsControls}>
                                    <div className={styles.listingSearchFields}>
                                      <label className={styles.listingSearchBox}>
                                        <Search size={15} />
                                        <input
                                          value={listingSearch.section}
                                          onChange={(event) => handleListingSearchChange(row.event_id, "section", event.target.value)}
                                          placeholder="Search section"
                                          aria-label="Search listings by section"
                                        />
                                      </label>
                                      <label className={styles.listingSearchBox}>
                                        <Search size={15} />
                                        <input
                                          value={listingSearch.row}
                                          onChange={(event) => handleListingSearchChange(row.event_id, "row", event.target.value)}
                                          placeholder="Search row"
                                          aria-label="Search listings by row"
                                        />
                                      </label>
                                    </div>
                                    <div className={styles.listingPager}>
                                      <span>
                                        Showing {filteredListings.length === 0 ? 0 : listingPageStart + 1}
                                        -{listingPageEnd} of {filteredListings.length.toLocaleString()}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => setListingPage(row.event_id, listingPageIndex - 1)}
                                        disabled={listingPageIndex === 0}
                                      >
                                        <ChevronLeft size={15} />
                                      </button>
                                      <span>Page {listingPageIndex + 1} of {listingPageCount}</span>
                                      <button
                                        type="button"
                                        onClick={() => setListingPage(row.event_id, listingPageIndex + 1)}
                                        disabled={listingPageIndex + 1 >= listingPageCount}
                                      >
                                        <ChevronRight size={15} />
                                      </button>
                                    </div>
                                  </div>
                                  {filteredListings.length === 0 ? (
                                    <div className={styles.listingsEmpty}>No listings match the section and row filters.</div>
                                  ) : (
                                    <div className={styles.listingsTableWrap}>
                                      <div className={styles.listingsTable} role="table" aria-label={`${row.event_name} listings`}>
                                        <div className={styles.listingsTableHeader} role="row">
                                        <div role="columnheader">Listing ID</div>
                                        <div role="columnheader">Section / Row</div>
                                        <div role="columnheader">Qty</div>
                                          <div role="columnheader">Markup %</div>
                                          <div role="columnheader">Listing Price (TE)</div>
                                          <div role="columnheader">Displaying Price</div>
                                          <div role="columnheader">Action</div>
                                        </div>
                                        <div role="rowgroup">
                                          {visibleListings.map((listing) => {
                                            const listingId = normalizeListingId(listing);
                                            const key = getListingKey(row.event_id, listingId);
                                            const isCarouselListing =
                                              carouselConfigurationByEventId[row.event_id]?.listing.listing_id === listingId;
                                            const basePrice = getListingBasePrice(listing);
                                            const eventMarkup = Number(
                                              listing.event_markup_percentage ??
                                                listing.markup_percentage ??
                                                row.markup_percentage ??
                                                0,
                                            );
                                            const fallbackMarkup = getListingMarkupPercentage(listing);
                                            const inputValue = listingMarkupInputs[key] ?? String(fallbackMarkup);
                                            const eventMarkedPrice = applyMarkup(basePrice, eventMarkup);
                                            const displayPrice = Number(
                                              listing.display_price ??
                                                listing.customer_price ??
                                                applyPriceAdjustment(eventMarkedPrice, Number(listing.price_adjustment_amount ?? 0)),
                                            );

                                            return (
                                              <div className={styles.listingsTableRow} role="row" key={key}>
                                                <div className={styles.listingsTableCell} role="cell">
                                                  <strong>{listingId || "-"}</strong>
                                                  {listing.listing_markup_override_applied && (
                                                    <div className={styles.overrideBadge}>Override</div>
                                                  )}
                                                  {isCarouselListing && (
                                                    <div className={styles.carouselBadge}>Carousel</div>
                                                  )}
                                                </div>
                                                <div className={styles.listingsTableCell} role="cell">
                                                  <div className={styles.listingMeta}>
                                                    <span>{listing.section || "N/A"}</span>
                                                    <span>{listing.row || "N/A"}</span>
                                                  </div>
                                                  {listing.seats && <div className={styles.subText}>{listing.seats}</div>}
                                                </div>
                                                <div className={styles.listingsTableCell} role="cell">{listing.quantity ?? listing.available_quantity ?? 0}</div>
                                                <div className={`${styles.listingsTableCell} ${styles.listingMarkupCell}`} role="cell">
                                                    <div className={styles.listingMarkupInput}>
                                                    <span>%</span>
                                                    <input
                                                      type="number"
                                                      step="0.01"
                                                      inputMode="decimal"
                                                      value={inputValue}
                                                      onChange={(event) => handleListingMarkupChange(row.event_id, listingId, event.target.value)}
                                                    />
                                                  </div>
                                                </div>
                                                <div className={`${styles.listingsTableCell} ${styles.priceCell}`} role="cell">{formatCurrency(basePrice)}</div>
                                                <div className={`${styles.listingsTableCell} ${styles.priceCell}`} role="cell">{formatCurrency(displayPrice)}</div>
                                                <div className={styles.listingsTableCell} role="cell">
                                                  <div className={styles.listingActions}>
                                                    <button
                                                      className={styles.saveListingButton}
                                                      onClick={() => saveListingMarkup(row, listing)}
                                                      disabled={Boolean(savingListingByKey[key])}
                                                    >
                                                      {savingListingByKey[key] ? "Saving..." : "Save Markup"}
                                                    </button>
                                                    <button
                                                      className={isCarouselListing ? styles.selectedCarouselButton : styles.carouselButton}
                                                      onClick={() => openCarouselListing(row, listing)}
                                                      title={isCarouselListing ? "Edit carousel configuration" : "Add listing to carousel"}
                                                      aria-label={isCarouselListing ? "Edit carousel configuration" : "Add listing to carousel"}
                                                    >
                                                      {isCarouselListing ? "Edit carousel" : "Add to carousel"}
                                                    </button>
                                                    <button
                                                      className={styles.removeCarouselButton}
                                                      onClick={() => void removeFromCarousel(row)}
                                                      disabled={!isCarouselListing || savingLandingSelection || removingCarouselEventId != null}
                                                      title={isCarouselListing ? "Remove this event from the carousel" : "This listing is not in the carousel"}
                                                      aria-label="Remove from carousel"
                                                    >
                                                      {removingCarouselEventId === row.event_id ? "Removing..." : "Remove from carousel"}
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.footer}>
          <span>{paginationLabel}</span>
          <div className={styles.pagination}>
            <button disabled={page === 0 || loading} onClick={() => setPage((prev) => Math.max(0, prev - 1))}>
              <ChevronLeft size={16} />
            </button>
            <div className={styles.jumpControl}>
              <span>Go to</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={jumpPage}
                onChange={(event) => setJumpPage(event.target.value)}
                onKeyDown={(event) => { if (event.key === "Enter") handleJumpToPage(); }}
              />
              <button disabled={loading} onClick={handleJumpToPage}>Go</button>
            </div>
            <button disabled={page + 1 >= totalPages || loading} onClick={() => setPage((prev) => prev + 1)}>
              <ChevronRight size={16} />
            </button>
            <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}>
              {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size} / page</option>)}
            </select>
          </div>
        </div>
      </div>

      {selectedEvent && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div>
                <h2>{selectedEventTitle}</h2>
                {editEvents.length > 1 ? (
                  <p>Apply the same markup percentage to all selected events.</p>
                ) : (
                  <p>{selectedEvent.venue_name} - {formatEventSourceDateTimeDisplay(pickEventDateSource(selectedEvent))}</p>
                )}
              </div>
              <button className={styles.closeIconButton} onClick={closeEdit} aria-label="Close markup editor" title="Close">
                <X size={18} />
              </button>
            </div>

            <div className={styles.modalGrid}>
              <div className={styles.editPanel}>
                {modalSuccessMessage && <div className={styles.modalSuccessBanner}>{modalSuccessMessage}</div>}
                <label>Markup Percentage</label>
                <div className={styles.percentInput}>
                  <input
                    type="number"
                    step="0.01"
                    value={markupInput}
                    onChange={(event) => setMarkupInput(event.target.value)}
                  />
                  <span>%</span>
                </div>
                <label>Change Note</label>
                <textarea
                  value={noteInput}
                  onChange={(event) => setNoteInput(event.target.value)}
                  placeholder="Optional note for audit trail"
                />
                <button className={styles.saveButton} onClick={saveMarkup} disabled={saving || Boolean(modalSuccessMessage)}>
                  {modalSuccessMessage ? "Saved" : saving ? "Saving..." : editEvents.length > 1 ? `Save ${editEvents.length} Events` : "Save Markup"}
                </button>
              </div>

              <div className={styles.auditPanel}>
                <div className={styles.auditTitle}>
                  <History size={16} />
                  Audit Trail
                </div>
                {editEvents.length > 1 ? (
                  <div className={styles.emptyAudit}>Audit history is shown when editing one event.</div>
                ) : auditRows.length === 0 ? (
                  <div className={styles.emptyAudit}>No changes recorded.</div>
                ) : (
                  auditRows.map((row) => (
                    <div key={row.id} className={styles.auditRow}>
                      <strong>{formatPercent(row.old_markup_percentage)} to {formatPercent(row.new_markup_percentage)}</strong>
                      <span>{row.changed_by} {row.changed_role ? `(${row.changed_role})` : ""}</span>
                      <small>{formatAuditDateTime(row.created_at)}</small>
                      {row.note && <p>{row.note}</p>}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {carouselListingModal && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={`${styles.modal} ${styles.carouselModal}`} role="dialog" aria-modal="true" aria-labelledby="carousel-modal-title">
            <div className={styles.modalHeader}>
              <div>
                <h2 id="carousel-modal-title">Configure Homepage Carousel Listing</h2>
                <p>{carouselListingModal.event.event_name}</p>
              </div>
              <button
                className={styles.closeIconButton}
                onClick={() => setCarouselListingModal(null)}
                aria-label="Close carousel listing editor"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className={styles.carouselModalBody}>
              <div className={styles.carouselListingSummary}>
                <div><span>Listing ID</span><strong>{carouselListingModal.listing.listing_id}</strong></div>
                <div><span>Section / Row</span><strong>{carouselListingModal.listing.section || "N/A"} / {carouselListingModal.listing.row || "N/A"}</strong></div>
                <div><span>Quantity</span><strong>{carouselListingModal.listing.quantity}</strong></div>
                <div><span>TE Price</span><strong>{formatCurrency(carouselListingModal.listing.te_price)}</strong></div>
                <div><span>QuickAsyst Price</span><strong>{formatCurrency(carouselListingModal.listing.quickasyst_price)}</strong></div>
              </div>

              <div className={styles.competitorHeading}>
                <div>
                  <h3>Select exactly 3 competitors</h3>
                  <p>Enter the manually verified price for each selected marketplace.</p>
                </div>
                <strong>{carouselListingModal.selectedCompetitors.length}/3 selected</strong>
              </div>

              <div className={styles.competitorGrid}>
                {CAROUSEL_COMPETITORS.map((competitor) => {
                  const selected = carouselListingModal.selectedCompetitors.includes(competitor.key);
                  const selectionLimitReached = !selected && carouselListingModal.selectedCompetitors.length >= 3;
                  return (
                    <div
                      key={competitor.key}
                      className={`${styles.competitorOption} ${selected ? styles.competitorOptionSelected : ""}`}
                    >
                      <label>
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={selectionLimitReached}
                          onChange={() => toggleCarouselCompetitor(competitor.key)}
                        />
                        <span>{competitor.label}</span>
                      </label>
                      {selected && (
                        <div className={styles.currencyInput}>
                          <span>$</span>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            inputMode="decimal"
                            aria-label={`${competitor.label} price`}
                            value={carouselListingModal.priceInputs[competitor.key] || ""}
                            onChange={(event) => setCarouselListingModal((current) => current ? ({
                              ...current,
                              priceInputs: {
                                ...current.priceInputs,
                                [competitor.key]: event.target.value,
                              },
                            }) : current)}
                            placeholder="0.00"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className={styles.carouselModalActions}>
                <button className={styles.closeButton} onClick={() => setCarouselListingModal(null)}>Cancel</button>
                <button
                  className={styles.saveButton}
                  onClick={() => void saveCarouselListingDraft()}
                  disabled={carouselListingModal.selectedCompetitors.length !== 3 || savingLandingSelection}
                >
                  {savingLandingSelection ? "Saving..." : "Save Listing Configuration"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyAdminEventsMarkup;

